import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Edit, Trash2 } from 'lucide-react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import { isTenantAdminOrSystemAdmin } from '../../../shared/auth/access';
import './AccountingVouchersPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    fetchError: 'Muhasebe fisleri alinamadi.',
    fetchErrorDetail: 'Muhasebe fisleri yuklenirken hata olustu.',
    linesFetchError: 'Fis detaylari alinamadi.',
    linesFetchErrorDetail: 'Fis detaylari yuklenirken hata olustu.',
    companyPeriodRequired: 'Sirket ve donem secilmelidir.',
    docNoDateRequired: 'Fis no ve tarih zorunludur.',
    unbalancedError: 'Muhasebe fisi borc ve alacak toplamlari esit olmalidir. Fis kaydedilemez.',
    saveError: 'Fis kaydedilemedi.',
    updated: 'Fis guncellendi.',
    created: 'Yeni fis kaydedildi.',
    deleteConfirm: 'Bu fisi silmek istediginizden emin misiniz?',
    deletePermanentConfirm: 'KALICI SILME: Bu fis veritabanindan tamamen silinecek ve geri alinamaz. Devam etmek istiyor musunuz?',
    deleteError: 'Fis silinemedi.',
    deleteErrorDetail: 'Fis silinirken hata olustu.',
    deletedVouchers: 'Silinmis Fisler',
    showDeleted: 'Silinenleri Goster',
    hideDeleted: 'Silinenleri Gizle',
    hardDelete: 'Kalici Sil',
    hardDeleteConfirm: 'Bu fis veritabanindan KALICI olarak silinecek ve geri alinamaz. Emin misiniz?',
    clearData: 'Verileri Temizle',
    clearDataConfirm: 'Bu sirket ve donem icin aktarilan muhasebe fisleri kalici olarak silinecek. Devam edilsin mi?',
    clearDataDone: 'Aktarilan fis verileri temizlendi. Silinen fis: {{count}}',
    clearDataError: 'Aktarilan fis verileri temizlenemedi.',
    deletedFetchError: 'Silinmis fisler alinamadi.',
    noDeletedVouchers: 'Silinmis fis bulunamadi.',
    deletedAt: 'Silinme Tarihi',
    periodFallback: 'Donem {{id}}',
    headerFor: '{{scope}} icin accounting_vouchers verileri',
    headerDefault: 'Muhasebe fislerini tek satir olarak listeleyin, satir detaylarini formda inceleyin.',
    refresh: 'Yenile',
    newVoucher: 'Yeni Fis',
    title: 'Muhasebe Fisleri',
    voucherDate: 'Fis Tarihi',
    voucherNo: 'Fis No',
    voucherNoPlaceholder: 'Orn. MF-1001',
    voucherType: 'Fis Turu',
    status: 'Durum',
    line: 'Satir',
    accountCode: 'Hesap Kodu',
    accountName: 'Hesap Adi',
    debit: 'Borc',
    credit: 'Alacak',
    description: 'Aciklama',
    action: 'Islem',
    selectAccount: 'Hesap secin',
    existingAccount: '(mevcut)',
    accountAutoFill: 'Hesap kodu secildiginde otomatik dolar',
    removeLine: 'Satiri Sil',
    addLine: 'Satir Ekle',
    totalDebit: 'Toplam Borc',
    totalCredit: 'Toplam Alacak',
    balanceStatus: 'Durum',
    balanced: 'Dengeli',
    unbalanced: 'Dengesiz',
    updateVoucher: 'Fisi Guncelle',
    saveNewVoucher: 'Yeni Fis Kaydet',
    close: 'Kapat',
    cancel: 'Iptal',
    searchLabel: 'Arama (Fis no, hesap kodu, aciklama)',
    searchPlaceholder: 'Orn. MF-1001 veya 100.01',
    dateRange: 'Fis Tarihi Araligi',
    dateStartAria: 'Fis tarihi baslangic',
    dateEndAria: 'Fis tarihi bitis',
    clearFilters: 'Filtreleri Temizle',
    voucherCount: 'Fis Sayisi',
    lineCount: 'Satir Sayisi',
    netBalance: 'Net Bakiye',
    trialBalanced: 'Mizan Dengeli',
    loadingData: 'Veriler yukleniyor...',
    emptyData: 'Bu filtrelere uygun muhasebe fisi bulunamadi.',
    type: 'Tur',
    review: 'Incele',
    edit: 'Duzenle',
    delete: 'Sil',
    draft: 'DRAFT',
    approved: 'APPROVED',
    posted: 'POSTED',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    fetchError: 'Accounting vouchers could not be fetched.',
    fetchErrorDetail: 'An error occurred while loading accounting vouchers.',
    linesFetchError: 'Voucher details could not be fetched.',
    linesFetchErrorDetail: 'An error occurred while loading voucher details.',
    companyPeriodRequired: 'Company and period must be selected.',
    docNoDateRequired: 'Voucher number and date are required.',
    unbalancedError: 'Debit and credit totals must be equal. Voucher cannot be saved.',
    saveError: 'Voucher could not be saved.',
    updated: 'Voucher updated.',
    created: 'New voucher saved.',
    deleteConfirm: 'Are you sure you want to delete this voucher?',
    deletePermanentConfirm: 'PERMANENT DELETE: This voucher will be completely removed from the database and cannot be recovered. Do you want to continue?',
    deleteError: 'Voucher could not be deleted.',
    deleteErrorDetail: 'An error occurred while deleting voucher.',
    deletedVouchers: 'Deleted Vouchers',
    showDeleted: 'Show Deleted',
    hideDeleted: 'Hide Deleted',
    hardDelete: 'Permanently Delete',
    hardDeleteConfirm: 'This voucher will be PERMANENTLY deleted from the database and cannot be recovered. Are you sure?',
    clearData: 'Clear Data',
    clearDataConfirm: 'Transferred accounting vouchers for this company and period will be permanently deleted. Continue?',
    clearDataDone: 'Transferred voucher data cleared. Deleted vouchers: {{count}}',
    clearDataError: 'Transferred voucher data could not be cleared.',
    deletedFetchError: 'Deleted vouchers could not be fetched.',
    noDeletedVouchers: 'No deleted vouchers found.',
    deletedAt: 'Deleted At',
    periodFallback: 'Period {{id}}',
    headerFor: 'Accounting voucher data for {{scope}}',
    headerDefault: 'List accounting vouchers in rows and review line details in the form.',
    refresh: 'Refresh',
    newVoucher: 'New Voucher',
    title: 'Accounting Vouchers',
    voucherDate: 'Voucher Date',
    voucherNo: 'Voucher No',
    voucherNoPlaceholder: 'Ex. MF-1001',
    voucherType: 'Voucher Type',
    status: 'Status',
    line: 'Line',
    accountCode: 'Account Code',
    accountName: 'Account Name',
    debit: 'Debit',
    credit: 'Credit',
    description: 'Description',
    action: 'Action',
    selectAccount: 'Select account',
    existingAccount: '(existing)',
    accountAutoFill: 'Auto-filled when account code is selected',
    removeLine: 'Remove Line',
    addLine: 'Add Line',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    balanceStatus: 'Status',
    balanced: 'Balanced',
    unbalanced: 'Unbalanced',
    updateVoucher: 'Update Voucher',
    saveNewVoucher: 'Save New Voucher',
    close: 'Close',
    cancel: 'Cancel',
    searchLabel: 'Search (voucher no, account code, description)',
    searchPlaceholder: 'Ex. MF-1001 or 100.01',
    dateRange: 'Voucher Date Range',
    dateStartAria: 'Voucher date start',
    dateEndAria: 'Voucher date end',
    clearFilters: 'Clear Filters',
    voucherCount: 'Voucher Count',
    lineCount: 'Line Count',
    netBalance: 'Net Balance',
    trialBalanced: 'Trial Balanced',
    loadingData: 'Loading data...',
    emptyData: 'No accounting vouchers found for these filters.',
    type: 'Type',
    review: 'View',
    edit: 'Edit',
    delete: 'Delete',
    draft: 'DRAFT',
    approved: 'APPROVED',
    posted: 'POSTED',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const buildJsonHeaders = () => ({ ...buildHeaders(), 'Content-Type': 'application/json' });

const normalizeDateForInput = (value) => {
  if (!value) return '';
  return value.includes('T') ? value.split('T')[0] : value;
};

const createEmptyLine = () => ({
  lineNo: null,
  accountCode: '',
  accountName: '',
  debit: '',
  credit: '',
  description: ''
});

const initialFormState = () => ({
  docDate: '',
  docNo: '',
  voucherType: 'MANUAL',
  status: 'DRAFT',
  lines: [createEmptyLine()]
});

const toAmount = (value) => {
  if (value === null || value === undefined) return 0;
  const normalized = value.toString().replace(',', '.').trim();
  if (normalized === '') return 0;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

function AccountingVouchersPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    const normalized = value == null ? 0 : Number(value);
    if (Number.isNaN(normalized)) return currencyFormatter.format(0);
    return currencyFormatter.format(normalized);
  }, [currencyFormatter]);

  const formatDate = useCallback((value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(localeKey, { year: 'numeric', month: '2-digit', day: '2-digit' });
  }, [localeKey]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [vouchers, setVouchers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [docDateFrom, setDocDateFrom] = useState('');
  const [docDateTo, setDocDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [formState, setFormState] = useState(initialFormState());
  const [formMode, setFormMode] = useState('create');
  const [editingVoucherId, setEditingVoucherId] = useState(null);
  const [formMessage, setFormMessage] = useState({ type: '', text: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedVouchers, setDeletedVouchers] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [clearingData, setClearingData] = useState(false);
  const canClearData = useMemo(() => isTenantAdminOrSystemAdmin(), []);
  const { canWrite } = useCompanyWriteAccess(selection.companyId);

  const fetchChartAccounts = useCallback(async () => {
    if (!selection.companyId) {
      setChartAccounts([]);
      return;
    }
    try {
      const params = new URLSearchParams({ companyId: selection.companyId });
      const response = await fetch(`${API_BASE_URL}/api/chart-of-accounts?${params}`, { headers: buildHeaders() });
      if (!response.ok) {
        setChartAccounts([]);
        return;
      }
      const data = await response.json();
      setChartAccounts(Array.isArray(data) ? data : []);
    } catch {
      setChartAccounts([]);
    }
  }, [selection.companyId]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (formOpen && selection.companyId) fetchChartAccounts();
  }, [formOpen, selection.companyId, fetchChartAccounts]);

  const handleSelectionChange = useCallback((value) => {
    setSelection({
      companyId: value?.companyId || '',
      periodId: value?.periodId || '',
      company: value?.company || null,
      period: value?.period || null
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) {
      setVouchers([]);
      setSummary(null);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    setStatus({ type: '', text: '' });

    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
      if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
      if (docDateFrom) params.append('docDateFrom', docDateFrom);
      if (docDateTo) params.append('docDateTo', docDateTo);

      const response = await fetch(`${API_BASE_URL}/api/transferred-data/accounting-vouchers?${params}`, { headers: buildHeaders() });
      const payload = await response.json();

      if (!response.ok) throw new Error(payload?.message || t('fetchError'));

      setVouchers(payload.vouchers || []);
      setSummary(payload.summary || null);
    } catch (caught) {
      setError(caught?.message || t('fetchErrorDetail'));
      setVouchers([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, debouncedSearch, docDateFrom, docDateTo, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const clearFilters = () => {
    setSearch('');
    setDocDateFrom('');
    setDocDateTo('');
  };

  const resetForm = () => {
    setFormState(initialFormState());
    setFormMode('create');
    setEditingVoucherId(null);
    setFormMessage({ type: '', text: '' });
  };

  const openCreateForm = () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    setStatus({ type: '', text: '' });
    resetForm();
    setFormOpen(true);
  };

  const openEditForm = async (voucher, mode) => {
    setFormLoading(true);
    setFormMessage({ type: '', text: '' });
    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
      const response = await fetch(`${API_BASE_URL}/api/transferred-data/accounting-vouchers/${voucher.voucherId}/lines?${params}`, { headers: buildHeaders() });
      const lines = await response.json();
      if (!response.ok) throw new Error(lines?.message || t('linesFetchError'));

      setFormState({
        docDate: normalizeDateForInput(voucher.docDate) || '',
        docNo: voucher.docNo || '',
        voucherType: voucher.voucherType || 'MANUAL',
        status: voucher.status || 'DRAFT',
        lines: lines.map((line) => ({
          lineNo: line.lineNo,
          accountCode: line.accountCode || '',
          accountName: line.accountName || '',
          debit: line.debit != null ? line.debit.toString().replace('.', ',') : '',
          credit: line.credit != null ? line.credit.toString().replace('.', ',') : '',
          description: line.description || ''
        }))
      });
      setFormMode(mode);
      setEditingVoucherId(voucher.voucherId);
      setFormOpen(true);
    } catch (caught) {
      setError(caught?.message || t('linesFetchErrorDetail'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewVoucher = (voucher) => openEditForm(voucher, 'view');
  const openEditableVoucher = (voucher) => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    openEditForm(voucher, 'edit');
  };

  const addLine = () => setFormState((prev) => ({ ...prev, lines: [...prev.lines, createEmptyLine()] }));

  const updateLine = (index, field, value) => {
    setFormState((prev) => {
      const next = { ...prev, lines: [...prev.lines] };
      const line = { ...next.lines[index], [field]: value };
      if (field === 'accountCode' && chartAccounts.length > 0) {
        const account = chartAccounts.find((a) => String(a.code).trim() === String(value).trim());
        if (account) line.accountName = account.name || '';
      }
      next.lines[index] = line;
      return next;
    });
  };

  const removeLine = (index) => setFormState((prev) => {
    if (prev.lines.length === 1) return prev;
    return { ...prev, lines: prev.lines.filter((_, idx) => idx !== index) };
  });

  const handleFormSubmit = async (event) => {
    event?.preventDefault();
    if (!canWrite) {
      setFormMessage({ type: 'error', text: t('readOnlyAccess') });
      return;
    }
    if (formMode === 'view') return;

    if (!selection.companyId || !selection.periodId) {
      setFormMessage({ type: 'error', text: t('companyPeriodRequired') });
      return;
    }

    if (!formState.docNo || !formState.docDate) {
      setFormMessage({ type: 'error', text: t('docNoDateRequired') });
      return;
    }

    const totalDebit = formState.lines.reduce((s, l) => s + toAmount(l.debit), 0);
    const totalCredit = formState.lines.reduce((s, l) => s + toAmount(l.credit), 0);
    if (Math.abs(totalDebit - totalCredit) >= 0.01) {
      setFormMessage({ type: 'error', text: t('unbalancedError') });
      return;
    }

    setFormLoading(true);
    setFormMessage({ type: '', text: '' });

    try {
      const payload = {
        docNo: formState.docNo,
        docDate: formState.docDate,
        voucherType: formState.voucherType,
        status: formState.status,
        lines: formState.lines.map((line, index) => ({
          lineNo: index + 1,
          accountCode: line.accountCode,
          accountName: line.accountName,
          debit: toAmount(line.debit),
          credit: toAmount(line.credit),
          description: line.description
        }))
      };

      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
      const url = formMode === 'edit'
        ? `${API_BASE_URL}/api/transferred-data/accounting-vouchers/${editingVoucherId}?${params}`
        : `${API_BASE_URL}/api/transferred-data/accounting-vouchers?${params}`;

      const response = await fetch(url, {
        method: formMode === 'edit' ? 'PUT' : 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.message || t('saveError'));

      setFormMessage({ type: 'success', text: formMode === 'edit' ? t('updated') : t('created') });
      resetForm();
      setFormOpen(false);
      fetchData();
    } catch (caught) {
      setFormMessage({ type: 'error', text: caught?.message || t('saveError') });
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const fetchDeletedVouchers = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return;
    setDeletedLoading(true);
    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
      const response = await fetch(`${API_BASE_URL}/api/transferred-data/accounting-vouchers/deleted?${params}`, { headers: buildHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || t('deletedFetchError'));
      setDeletedVouchers(Array.isArray(data) ? data : []);
    } catch {
      setDeletedVouchers([]);
    } finally {
      setDeletedLoading(false);
    }
  }, [selection.companyId, selection.periodId, t]);

  const handleToggleDeleted = useCallback(() => {
    setShowDeleted((prev) => {
      if (!prev) fetchDeletedVouchers();
      return !prev;
    });
  }, [fetchDeletedVouchers]);

  const handleHardDeleteVoucher = async (voucherId) => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    setStatus({ type: '', text: '' });
    if (!window.confirm(t('hardDeleteConfirm'))) return;
    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId, permanent: 'true' });
      const response = await fetch(`${API_BASE_URL}/api/transferred-data/accounting-vouchers/${voucherId}?${params}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || t('deleteError'));
      }
      fetchDeletedVouchers();
    } catch (caught) {
      setError(caught?.message || t('deleteErrorDetail'));
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    setStatus({ type: '', text: '' });
    if (!window.confirm(t('deleteConfirm'))) return;

    if (!selection.companyId || !selection.periodId) {
      setError(t('companyPeriodRequired'));
      return;
    }

    const permanent = window.confirm(t('deletePermanentConfirm'));

    try {
      const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
      if (permanent) params.append('permanent', 'true');
      const response = await fetch(`${API_BASE_URL}/api/transferred-data/accounting-vouchers/${voucherId}?${params}`, {
        method: 'DELETE',
        headers: buildHeaders()
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || t('deleteError'));
      }

      fetchData();
    } catch (caught) {
      setError(caught?.message || t('deleteErrorDetail'));
    }
  };

  const companyLabel = selection.company?.name || '';

  const periodLabel = useMemo(() => {
    if (selection.period?.code) return selection.period.code;
    if (selection.period?.description) return selection.period.description;
    if (selection.period?.id) return t('periodFallback').replace('{{id}}', String(selection.period.id));
    return '';
  }, [selection.period, t]);

  const headerDescription = useMemo(() => {
    const parts = [];
    if (companyLabel) parts.push(companyLabel);
    if (periodLabel) parts.push(periodLabel);
    if (parts.length) return t('headerFor').replace('{{scope}}', parts.join(' � '));
    return t('headerDefault');
  }, [companyLabel, periodLabel, t]);

  const headerActions = (
    <>
      <button type="button" className="btn-secondary" onClick={handleRefresh} disabled={loading || !selection.companyId || !selection.periodId}>{t('refresh')}</button>
      {canClearData ? (
        <button
          type="button"
          className="btn-danger"
          onClick={async () => {
            if (!selection.companyId || !selection.periodId) {
              setError(t('companyPeriodRequired'));
              return;
            }
            if (!window.confirm(t('clearDataConfirm'))) {
              return;
            }
            setError('');
            setStatus({ type: '', text: '' });
            setClearingData(true);
            try {
              const params = new URLSearchParams({ companyId: selection.companyId, periodId: selection.periodId });
              const response = await fetch(`${API_BASE_URL}/api/transferred-data/accounting-vouchers/clear?${params}`, {
                method: 'POST',
                headers: buildHeaders()
              });
              const payload = await response.json().catch(() => ({}));
              if (!response.ok) {
                throw new Error(payload?.message || t('clearDataError'));
              }
              const count = Number(payload?.deletedVoucherCount || 0);
              setStatus({ type: 'success', text: t('clearDataDone').replace('{{count}}', String(count)) });
              setShowDeleted(false);
              setDeletedVouchers([]);
              await fetchData();
            } catch (caught) {
              setError(caught?.message || t('clearDataError'));
            } finally {
              setClearingData(false);
            }
          }}
          disabled={loading || clearingData || !selection.companyId || !selection.periodId}
        >
          {clearingData ? `${t('clearData')}...` : t('clearData')}
        </button>
      ) : null}
      <button type="button" className="btn-primary" onClick={openCreateForm} disabled={!canWrite || loading || !selection.companyId || !selection.periodId}>{t('newVoucher')}</button>
    </>
  );

  const voucherCount = summary?.voucherCount ?? 0;
  const lineCount = summary?.lineCount ?? 0;
  const netBalance = summary?.netBalance ?? 0;
  const isBalanced = summary?.balanced ?? true;
  const formModeIsView = formMode === 'view';

  const formTotalDebit = formState.lines.reduce((sum, l) => sum + toAmount(l.debit), 0);
  const formTotalCredit = formState.lines.reduce((sum, l) => sum + toAmount(l.credit), 0);
  const formIsBalanced = Math.abs(formTotalDebit - formTotalCredit) < 0.01;

  return (
    <div className="accounting-vouchers">
      <PageHeader title={t('title')} description={headerDescription} actions={headerActions} />

      {formOpen && (
        <form className="accounting-vouchers__form" onSubmit={handleFormSubmit}>
          <div className="accounting-vouchers__form-grid">
            <label>
              {t('voucherDate')}
              <input type="date" value={formState.docDate} disabled={formModeIsView} onChange={(event) => setFormState((prev) => ({ ...prev, docDate: event.target.value }))} />
            </label>
            <label>
              {t('voucherNo')}
              <input type="text" placeholder={t('voucherNoPlaceholder')} value={formState.docNo} disabled={formModeIsView} onChange={(event) => setFormState((prev) => ({ ...prev, docNo: event.target.value }))} />
            </label>
            <label>
              {t('voucherType')}
              <input type="text" value={formState.voucherType} disabled={formModeIsView} onChange={(event) => setFormState((prev) => ({ ...prev, voucherType: event.target.value }))} />
            </label>
            <label>
              {t('status')}
              <select value={formState.status} disabled={formModeIsView} onChange={(event) => setFormState((prev) => ({ ...prev, status: event.target.value }))}>
                <option value="DRAFT">{t('draft')}</option>
                <option value="APPROVED">{t('approved')}</option>
                <option value="POSTED">{t('posted')}</option>
              </select>
            </label>
          </div>

          <div className="accounting-vouchers__line-table-wrapper">
            <table className="accounting-vouchers__line-table">
              <thead>
                <tr>
                  <th>{t('line')}</th>
                  <th>{t('accountCode')}</th>
                  <th>{t('accountName')}</th>
                  <th>{t('debit')}</th>
                  <th>{t('credit')}</th>
                  <th>{t('description')}</th>
                  {!formModeIsView && <th>{t('action')}</th>}
                </tr>
              </thead>
              <tbody>
                {formState.lines.map((line, index) => (
                  <tr key={`${line.accountCode}-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      {formModeIsView ? (
                        <input type="text" value={line.accountCode} readOnly disabled />
                      ) : (
                        <select value={line.accountCode} onChange={(e) => updateLine(index, 'accountCode', e.target.value)} className="accounting-vouchers__account-select">
                          <option value="">{t('selectAccount')}</option>
                          {chartAccounts.map((acc) => (
                            <option key={acc.id} value={acc.code}>{acc.code} - {acc.name}</option>
                          ))}
                          {line.accountCode && !chartAccounts.some((a) => String(a.code).trim() === String(line.accountCode).trim()) && (
                            <option value={line.accountCode}>{line.accountCode} - {line.accountName || t('existingAccount')}</option>
                          )}
                        </select>
                      )}
                    </td>
                    <td>
                      <input type="text" value={line.accountName} disabled readOnly placeholder={t('accountAutoFill')} className="accounting-vouchers__account-name-readonly" />
                    </td>
                    <td><input type="text" inputMode="decimal" value={line.debit} disabled={formModeIsView} onChange={(event) => updateLine(index, 'debit', event.target.value)} /></td>
                    <td><input type="text" inputMode="decimal" value={line.credit} disabled={formModeIsView} onChange={(event) => updateLine(index, 'credit', event.target.value)} /></td>
                    <td><input type="text" value={line.description} disabled={formModeIsView} onChange={(event) => updateLine(index, 'description', event.target.value)} /></td>
                    {!formModeIsView && (
                      <td>
                        <button type="button" className="btn-secondary" onClick={() => removeLine(index)} disabled={formState.lines.length === 1}>{t('removeLine')}</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {!formModeIsView && (
              <div className="accounting-vouchers__line-actions">
                <button type="button" className="btn-secondary" onClick={addLine}>{t('addLine')}</button>
              </div>
            )}
          </div>

          <div className="accounting-vouchers__totals">
            <div className="accounting-vouchers__totals-row">
              <span className="accounting-vouchers__totals-label">{t('totalDebit')}</span>
              <strong className="accounting-vouchers__totals-value">{formatCurrency(formTotalDebit)}</strong>
            </div>
            <div className="accounting-vouchers__totals-row">
              <span className="accounting-vouchers__totals-label">{t('totalCredit')}</span>
              <strong className="accounting-vouchers__totals-value">{formatCurrency(formTotalCredit)}</strong>
            </div>
            <div className={`accounting-vouchers__totals-row accounting-vouchers__totals-balance ${formIsBalanced ? 'balanced' : 'unbalanced'}`}>
              <span className="accounting-vouchers__totals-label">{t('balanceStatus')}</span>
              <span className="accounting-vouchers__totals-pill">{formIsBalanced ? t('balanced') : t('unbalanced')}</span>
            </div>
          </div>

          {formMessage.text && (
            <p className={`accounting-vouchers__form-status ${formMessage.type === 'error' ? 'accounting-vouchers__form-status--error' : 'accounting-vouchers__form-status--success'}`}>
              {formMessage.text}
            </p>
          )}

          <div className="accounting-vouchers__form-actions">
            {!formModeIsView && (
              <button type="submit" className="btn-primary" disabled={formLoading}>{formMode === 'edit' ? t('updateVoucher') : t('saveNewVoucher')}</button>
            )}
            <button type="button" className="btn-danger" onClick={handleCloseForm} disabled={formLoading}>{formModeIsView ? t('close') : t('cancel')}</button>
          </div>
        </form>
      )}

      <CompanyPeriodSelector onSelectionChange={handleSelectionChange} />

      <div className="accounting-vouchers__filters">
        <div className="accounting-vouchers__filter-grid">
          <label>
            {t('searchLabel')}
            <input type="search" placeholder={t('searchPlaceholder')} value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <label>
            {t('dateRange')}
            <div className="accounting-vouchers__date-range">
              <input type="date" value={docDateFrom} aria-label={t('dateStartAria')} onChange={(event) => setDocDateFrom(event.target.value)} />
              <input type="date" value={docDateTo} aria-label={t('dateEndAria')} onChange={(event) => setDocDateTo(event.target.value)} />
            </div>
          </label>
        </div>
        <div className="accounting-vouchers__filter-actions">
          <button type="button" className="btn-secondary" onClick={clearFilters} disabled={loading}>{t('clearFilters')}</button>
        </div>
      </div>

      <div className="accounting-vouchers__summary-grid">
        <div className="accounting-vouchers__summary-card"><p>{t('voucherCount')}</p><strong>{voucherCount}</strong></div>
        <div className="accounting-vouchers__summary-card"><p>{t('lineCount')}</p><strong>{lineCount}</strong></div>
        <div className="accounting-vouchers__summary-card"><p>{t('totalDebit')}</p><strong>{formatCurrency(summary?.totalDebit)}</strong></div>
        <div className="accounting-vouchers__summary-card"><p>{t('totalCredit')}</p><strong>{formatCurrency(summary?.totalCredit)}</strong></div>
        <div className={`accounting-vouchers__summary-card accounting-vouchers__net-balance ${netBalance >= 0 ? 'debit' : 'credit'}`}>
          <p>{t('netBalance')}</p>
          <strong>{formatCurrency(netBalance)}</strong>
          <span className="accounting-vouchers__pill">{isBalanced ? t('trialBalanced') : t('unbalanced')}</span>
        </div>
      </div>

      {error && <p className="accounting-vouchers__status accounting-vouchers__status--error">{error}</p>}
      {status.text && <p className={`accounting-vouchers__status accounting-vouchers__status--${status.type}`}>{status.text}</p>}

      <div className="accounting-vouchers__table-wrapper">
        {loading ? (
          <div className="accounting-vouchers__loading"><p>{t('loadingData')}</p></div>
        ) : vouchers.length === 0 ? (
          <div className="accounting-vouchers__empty"><p>{t('emptyData')}</p></div>
        ) : (
          <div className="accounting-vouchers__table-scroll">
            <table className="accounting-vouchers__table">
              <thead>
                <tr>
                  <th>{t('voucherDate')}</th>
                  <th>{t('voucherNo')}</th>
                  <th>{t('status')}</th>
                  <th>{t('type')}</th>
                  <th>{t('lineCount')}</th>
                  <th className="text-right">{t('totalDebit')}</th>
                  <th className="text-right">{t('totalCredit')}</th>
                  <th className="text-right">{t('netBalance')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((voucher) => (
                  <tr key={voucher.voucherId}>
                    <td>{formatDate(voucher.docDate)}</td>
                    <td>{voucher.docNo}</td>
                    <td>{voucher.status || '-'}</td>
                    <td>{voucher.voucherType || '-'}</td>
                    <td>{voucher.lineCount}</td>
                    <td className="text-right">{formatCurrency(voucher.totalDebit)}</td>
                    <td className="text-right">{formatCurrency(voucher.totalCredit)}</td>
                    <td className="text-right">{formatCurrency(voucher.netBalance)}</td>
                    <td className="accounting-vouchers__row-actions">
                      <button type="button" className="accounting-vouchers__icon-button" onClick={() => handleViewVoucher(voucher)} title={t('review')}><Eye size={16} /></button>
                      <button type="button" className="accounting-vouchers__icon-button" onClick={() => openEditableVoucher(voucher)} disabled={!canWrite} title={t('edit')}><Edit size={16} /></button>
                      <button type="button" className="accounting-vouchers__icon-button accounting-vouchers__icon-button--danger" onClick={() => handleDeleteVoucher(voucher.voucherId)} disabled={!canWrite} title={t('delete')}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="accounting-vouchers__deleted-section">
        <div className="accounting-vouchers__deleted-header">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleToggleDeleted}
            disabled={!selection.companyId || !selection.periodId}
          >
            {showDeleted ? t('hideDeleted') : t('showDeleted')}
          </button>
        </div>

        {showDeleted && (
          <div className="accounting-vouchers__deleted-content">
            <h3 className="accounting-vouchers__deleted-title">{t('deletedVouchers')}</h3>
            {deletedLoading ? (
              <div className="accounting-vouchers__loading"><p>{t('loadingData')}</p></div>
            ) : deletedVouchers.length === 0 ? (
              <div className="accounting-vouchers__empty"><p>{t('noDeletedVouchers')}</p></div>
            ) : (
              <div className="accounting-vouchers__table-scroll">
                <table className="accounting-vouchers__table accounting-vouchers__table--deleted">
                  <thead>
                    <tr>
                      <th>{t('voucherDate')}</th>
                      <th>{t('voucherNo')}</th>
                      <th>{t('status')}</th>
                      <th>{t('type')}</th>
                      <th>{t('lineCount')}</th>
                      <th className="text-right">{t('totalDebit')}</th>
                      <th className="text-right">{t('totalCredit')}</th>
                      <th>{t('action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedVouchers.map((voucher) => (
                      <tr key={voucher.voucherId}>
                        <td>{formatDate(voucher.docDate)}</td>
                        <td>{voucher.docNo}</td>
                        <td>{voucher.status || '-'}</td>
                        <td>{voucher.voucherType || '-'}</td>
                        <td>{voucher.lineCount}</td>
                        <td className="text-right">{formatCurrency(voucher.totalDebit)}</td>
                        <td className="text-right">{formatCurrency(voucher.totalCredit)}</td>
                        <td className="accounting-vouchers__row-actions">
                          <button
                            type="button"
                            className="accounting-vouchers__icon-button accounting-vouchers__icon-button--danger"
                            onClick={() => handleHardDeleteVoucher(voucher.voucherId)}
                            disabled={!canWrite}
                            title={t('hardDelete')}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountingVouchersPage;
